/**
 * `Sidebar` — the Claude-style collapsible left rail that replaces
 * the old 48px icon-only `ActivityBar` AND the chunky top `Header`.
 *
 * Composition (top → bottom):
 *   1. `SidebarBrand` — `LexiunMark` glyph (always) + wordmark + org
 *      name (expanded only).
 *   2. Toggle chevron — top-right when expanded, top-centre when
 *      collapsed. `aria-expanded={sidebarExpanded}`.
 *   3. Top nav items: Home, Agent, Contacts, Pendências,
 *      Agendamentos, Activity. (Research/Pesquisa was removed
 *      2026-07-27 — operator directive; the `03_research/` workspace
 *      zone and its advisory Research-Trail widget are KEPT.)
 *   4. Reserved space (v2 — Recents list goes here later).
 *   5. Bottom admin/settings items: Agent, Organization (admin-only),
 *      Settings.
 *   6. `SidebarUtility` — Search (command-palette trigger), Gateway
 *      status, Theme toggle, Clerk `<UserButton />`. All four
 *      affordances live here per the Claude-style consolidation; the
 *      former floating `TopRightControls` cluster is retired.
 *
 * Three layout modes:
 *   - **Desktop expanded** (~256px): icon + label rows.
 *   - **Desktop collapsed** (~52px): icon-only rail with hover tooltips
 *     (`title={t(labelKey)}` already in place).
 *   - **Mobile**: drawer pattern. Closed = a small floating
 *     `sidebar-mobile-toggle` hamburger fixed top-left over the page.
 *     Open = full sidebar slides in as an `<aside>` with a backdrop;
 *     tap backdrop OR the in-drawer close button to dismiss. The
 *     store's `sidebarExpanded` value is ignored on mobile — the
 *     viewport always derives "drawer" mode locally.
 *
 * **Per-item testid prefix stays `activity-bar-*`** for backward compat
 * with the existing E2E suite (home-welcome.spec.ts +
 * 7-7-responsive.spec.ts). New testids introduced by this redesign
 * (`sidebar-toggle`, `sidebar-mobile-toggle`, `sidebar-utility-*`)
 * cover the surfaces that didn't exist before.
 *
 * Filename note: this file used to be `ActivityBar.tsx`. The export
 * was renamed to `Sidebar` but the file path stays so existing
 * imports don't break — `App.tsx` imports `Sidebar` from this module
 * directly.
 */

import {
  CalendarClock,
  ListChecks,
  Building2,
  Home,
  Bell,
  Menu,
  PanelLeft,
  PanelLeftClose,
  Settings,
  Users,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { type AppMode, type FocusPaneId, useUIStore } from "@/stores/useUIStore";
import { useTasklistGate } from "@/hooks/useTasklistGate";
import { useAuthIdentity } from "@/hooks/useAuthIdentity";
import { useViewportSize } from "@/hooks/useViewportSize";
import { cn } from "@/lib/utils";
import { homeTarget } from "@/lib/landing-decision";
import { useActiveContextStore } from "@/stores/useActiveContextStore";
import { AgentIncognitoIcon } from "@/components/icons/AgentIncognitoIcon";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { SidebarBrand } from "@/components/shell/SidebarBrand";
import { SidebarUtility } from "@/components/shell/SidebarUtility";

interface NavItem {
  mode: AppMode;
  icon: React.ElementType;
  /** i18n key resolved via `t(labelKey)` at render time. */
  labelKey: string;
  path: string;
  adminOnly?: boolean;
  /** Hidden until a runtime-gated feature is available (e.g. "tasklist"). */
  gated?: "tasklist";
}

const topItems: NavItem[] = [
  // Home is the ONE conversation entry (the separate "Chat"/global rail item was
  // removed 2026-07-25 — after the H2 redesign it duplicated Home for any lawyer
  // with no case selected). `path` here is the FIRST-RUN target only; for a
  // returning lawyer `homeTarget` (D-3) resolves it at click time to the case
  // they are in, else the global conversation (`/chat`) — see `handleClick`. The
  // global thread stays reachable from inside a case via the ContextIndicator's
  // "Global / Geral" switch, so it did not need its own rail button.
  { mode: "home", icon: Home, labelKey: "nav.home", path: "/home" },
  // The Agent Browser rail button was removed 2026-07-25: the agent's browser
  // now lives as the FIRST + DEFAULT tab of the Agent pane, so a standalone rail
  // entry beside "Agente" was redundant. The `/agent-browser` route + the
  // `agentBrowser` focus-pane slot remain (the agent-navigation discoverability
  // toast still opens them); only this rail entry is gone.
  // **Agent** — promoted out of the bottom configuration block (operator
  // direction 2026-07-21) to sit beside the browser. It is no longer only
  // settings: the page now fronts the agent's CHANNELS (email / Telegram /
  // WhatsApp), which is monitoring work the lawyer does often, not
  // configuration they touch once.
  { mode: "agent", icon: AgentIncognitoIcon, labelKey: "nav.agent", path: "/agent" },
  // Contacts program (lexiun-contacts SS7.1) — the lawyer's directory +
  // the Identity pane; top-level beside the workspace nav.
  // `Users` (two figures, no frame) rather than `BookUser` (a figure inside a
  // card outline): at the rail's 18px the frame collapsed into a grey box and
  // the person inside it stopped reading as a person (operator, 2026-07-27).
  // It is also already this repo's people glyph — ClientsPage, PartiesWidget,
  // RecentWorkWidget and NewClientDialog all use `Users` for the same meaning.
  { mode: "contacts", icon: Users, labelKey: "nav.contacts", path: "/contacts" },
  // Pendências — the user tasklist; top-level beside Contacts. Hidden when the
  // runtime gates the feature off (LEX_TASKLIST_ENABLE unset) via the filter
  // in the component body.
  { mode: "tasks", icon: ListChecks, labelKey: "nav.tasks", path: "/tasks", gated: "tasklist" },
  // Agendamentos — the CLOCK beside the LIST (recurring-jobs R2, agendamentos
  // plan §1): jobs the assistant runs on schedule. Sibling of Pendências by
  // design; opens the schedule focus pane. Kill-switch honesty lives in the
  // pane itself (SCHEDULE_DISABLED full-pane state), so the item is ungated.
  { mode: "schedule", icon: CalendarClock, labelKey: "nav.schedule", path: "/schedule" },
  { mode: "activity", icon: Bell, labelKey: "nav.activity", path: "/activity" },
];

/**
 * Modes that open in the FOCUS PANE instead of taking over the page. Keep in
 * step with `FocusPaneId` — the cast below is safe precisely because this list
 * is the same set.
 */
const FOCUS_PANE_MODES = ["tasks", "agentBrowser", "contacts", "agent", "schedule"] as const;

function isFocusPaneMode(mode: AppMode): mode is FocusPaneId {
  return (FOCUS_PANE_MODES as readonly string[]).includes(mode);
}

const bottomItems: NavItem[] = [
  { mode: "org", icon: Building2, labelKey: "nav.organization", path: "/org", adminOnly: true },
  { mode: "settings", icon: Settings, labelKey: "nav.settings", path: "/settings" },
];

function modeFromPath(pathname: string): AppMode {
  if (pathname.startsWith("/workspace")) return "cases";
  if (pathname.startsWith("/cases")) return "cases";
  if (pathname.startsWith("/clients")) return "cases";
  // The global thread `/chat` highlights HOME now that Home is the single
  // conversation entry (the standalone Chat rail item was removed 2026-07-25).
  if (pathname.startsWith("/chat")) return "home";
  // Order matters: /agent-browser must come BEFORE /agent so a hit
  // on the browser route doesn't fall through to the agent settings.
  if (pathname.startsWith("/agent-browser")) return "agentBrowser";
  if (pathname.startsWith("/agent")) return "agent";
  if (pathname.startsWith("/contacts")) return "contacts";
  if (pathname.startsWith("/identity")) return "contacts";
  if (pathname.startsWith("/tasks")) return "tasks";
  if (pathname.startsWith("/activity")) return "activity";
  if (pathname.startsWith("/org")) return "org";
  if (pathname.startsWith("/settings")) return "settings";
  return "home";
}

export function Sidebar() {
  const { t } = useTranslation();
  const activeMode = useUIStore((s) => s.activeMode);
  const setActiveMode = useUIStore((s) => s.setActiveMode);
  const sidebarExpanded = useUIStore((s) => s.sidebarExpanded);
  const toggleFocusPane = useUIStore((s) => s.toggleFocusPane);
  const closeFocusPane = useUIStore((s) => s.closeFocusPane);
  const focusPane = useUIStore((s) => s.focusPane);
  const landing = useUIStore((s) => s.landing);
  const selection = useActiveContextStore((s) => s.selection);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const navigate = useNavigate();
  const location = useLocation();
  const { identity } = useAuthIdentity();
  const isOrgAdmin = identity?.isOrgAdmin ?? false;
  const tasklistGate = useTasklistGate();

  const viewport = useViewportSize();
  const isMobile = viewport === "mobile";
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Mobile derivation lives here (not in the store). On mobile the
  // sidebar is ALWAYS a drawer; the persisted `sidebarExpanded` flag
  // only governs desktop. Inside the drawer body we render the
  // "expanded" content shape (labels visible) regardless.
  const expanded = isMobile ? true : sidebarExpanded;

  // Sync mode from URL on navigation (deep links, browser back).
  useEffect(() => {
    const modeFromUrl = modeFromPath(location.pathname);
    if (modeFromUrl !== activeMode) {
      setActiveMode(modeFromUrl);
    }
  }, [location.pathname, activeMode, setActiveMode]);

  function handleClick(item: NavItem) {
    // FOCUS-PANE surfaces open beside the conversation rather than navigating:
    // the point is to work them while prompting the concierge, and routing away
    // would close the very chat the lawyer is talking to. Their routes still
    // exist for a hand-typed URL / full-page view.
    if (isFocusPaneMode(item.mode)) {
      toggleFocusPane(item.mode);
      if (isMobile) setMobileDrawerOpen(false);
      return;
    }
    // D-3 (miniplan H2): Home means "my desk", not "the launcher". For a
    // returning lawyer it resolves to the case they are in, else the global
    // conversation; only a genuine first-run lawyer still lands on the welcome.
    // `activeMode` is corrected by the `modeFromPath` effect right after the
    // navigate, so the optimistic `setActiveMode` below cannot strand the
    // SidePanel on home content.
    const target =
      item.mode === "home"
        ? homeTarget({
            // Both inputs read imperatively AT CLICK TIME, matching the other
            // `homeTarget` call site in `CommandPalette`. Subscribing to
            // `landing` would re-render the whole rail when the boot verdict
            // lands, for a value nothing here renders.
            selection: useActiveContextStore.getState().selection,
            landing: useUIStore.getState().landing,
          })
        : item.path;
    // A route item EXITS split mode (operator 2026-07-25): tapping Home (or any
    // page item) while a focus pane is open closes the pane and goes full-page.
    // This matters most for Home, whose target `/chat` is itself a chat-left
    // route — so `FocusPaneHost` would keep the pane open BESIDE it and the
    // lawyer would never actually leave the split. Closing here makes "tap Home
    // → full focus on the conversation" literal.
    if (focusPane) closeFocusPane();
    setActiveMode(item.mode);
    navigate(target);
    if (isMobile) setMobileDrawerOpen(false);
  }

  const visibleBottomItems = bottomItems.filter(
    (item) => !item.adminOnly || isOrgAdmin,
  );

  // A focus-pane item is active while its pane is open. A ROUTE item (Home,
  // research, settings…) is active only when NO pane is open: a pane is
  // split-mode, and split-mode belongs to the pane's item, not the underlying
  // route. Without the `focusPane === null` guard, Home stayed lit beside an
  // open pane on `/chat` — "Home means full focus on the conversation, no
  // split" (operator 2026-07-25). Shared by both nav groups so they can't drift.
  // ⚖ Home is ALSO active when its D-3 target is where the lawyer already is
  // (ui-tester I-8, run ui-20260903-2241). Inside a case, `homeTarget` resolves
  // to that case's own conversation — "Home means my desk, not the launcher" —
  // so the click is a correct no-op that LOOKS broken: it was clicked twice in
  // a case and twice from /chat with nothing moving.
  //
  // The operator's outcome allowed either leaving the case or rendering Home as
  // the active mode. This takes the second, deliberately: the first would
  // overturn D-3, which is a ruling and not this lane's to reverse. Lighting the
  // item says "you are already here", which is true, instead of offering a
  // journey that does not exist.
  // ⚠ SUBSCRIBED, not read imperatively. `handleClick` may use `getState()`
  // because it runs at click time; this value is RENDERED, so an imperative
  // read would leave Home lit (or dark) after a selection change that did not
  // also change the route — a stale affordance is the defect this fixes, not a
  // shape to reintroduce one level up.
  const homeIsHere =
    focusPane === null && location.pathname === homeTarget({ selection, landing });

  const isItemActive = (item: NavItem) =>
    isFocusPaneMode(item.mode)
      ? focusPane === item.mode
      : focusPane === null && (activeMode === item.mode || (item.mode === "home" && homeIsHere));

  // Sidebar body (shared between desktop in-flow + mobile drawer).
  // On desktop it sits inside the shell flex row; on mobile it's
  // absolutely positioned inside the drawer wrapper below.
  const body = (
    <nav
      data-testid="activity-bar"
      data-layout={isMobile ? "drawer" : sidebarExpanded ? "expanded" : "collapsed"}
      aria-label={t("nav.modesAriaLabel")}
      className={cn(
        "flex h-full flex-col bg-cream-50 dark:bg-coffee-900",
        // Width transition lives on the outer wrapper; this inner
        // nav fills its parent. Border separates from main content
        // on desktop in-flow only — drawer has its own shadow.
        !isMobile && "border-r border-cream-200/60 dark:border-coffee-700/60",
      )}
    >
      {/* Brand + toggle row. On desktop the toggle sits at the top:
          top-right when expanded, top-centre when collapsed. On mobile
          the in-drawer close button replaces the chevron toggle. */}
      <div
        className={cn(
          "flex items-center",
          expanded ? "justify-between px-2" : "flex-col gap-1 px-1 pt-2",
        )}
      >
        <SidebarBrand expanded={expanded} />
        {isMobile ? (
          <button
            type="button"
            data-testid="sidebar-mobile-close"
            onClick={() => setMobileDrawerOpen(false)}
            aria-label={t("nav.closeSidebar")}
            className="m-2 flex h-8 w-8 items-center justify-center rounded-md text-coffee-500 transition-colors hover:bg-cream-100/60 hover:text-coffee-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 dark:text-cream-500 dark:hover:bg-coffee-800/60 dark:hover:text-cream-300"
          >
            <X size={16} />
          </button>
        ) : (
          <button
            type="button"
            data-testid="sidebar-toggle"
            onClick={toggleSidebar}
            aria-label={t("nav.toggleSidebar")}
            aria-expanded={sidebarExpanded}
            title={t("nav.toggleSidebar")}
            className="m-2 flex h-7 w-7 items-center justify-center rounded-md text-coffee-500 transition-colors hover:bg-cream-100/60 hover:text-coffee-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 dark:text-cream-500 dark:hover:bg-coffee-800/60 dark:hover:text-cream-300"
          >
            {sidebarExpanded ? <PanelLeftClose size={18} /> : <PanelLeft size={18} />}
          </button>
        )}
      </div>

      {/* Top nav items — extra `pt-6` separates the nav block from the
       *  brand row above so the first item doesn't crowd the wordmark. */}
      <div className={cn("flex flex-col gap-0.5 px-2 pt-6", !expanded && "px-1")}>
        {topItems
          .filter((item) => item.gated !== "tasklist" || tasklistGate === "enabled")
          .map((item) => (
          <NavRow
            key={item.mode}
            item={item}
            active={isItemActive(item)}
            expanded={expanded}
            onClick={() => handleClick(item)}
          />
        ))}
      </div>

      {/* Reserved Recents space (v2; left blank for now). */}
      <div className="flex-1" />

      {/* Bottom admin/settings items + utility row */}
      <div className={cn("flex flex-col gap-0.5 px-2", !expanded && "px-1")}>
        {visibleBottomItems.map((item) => (
          <NavRow
            key={item.mode}
            item={item}
            active={isItemActive(item)}
            expanded={expanded}
            onClick={() => handleClick(item)}
          />
        ))}
      </div>
      <SidebarUtility expanded={expanded} />
    </nav>
  );

  // --- Mobile: drawer pattern ----------------------------------------
  if (isMobile) {
    return (
      <>
        {/* Floating hamburger — visible only when the drawer is closed.
            Fixed top-left so it sits over whatever page content is
            rendering. Disappears when the drawer is open (the
            in-drawer X is the close affordance). */}
        {!mobileDrawerOpen && (
          <button
            type="button"
            data-testid="sidebar-mobile-toggle"
            onClick={() => setMobileDrawerOpen(true)}
            aria-label={t("nav.openSidebar")}
            aria-expanded={false}
            className="fixed left-3 top-3 z-30 flex h-9 w-9 items-center justify-center rounded-md bg-cream-50 text-coffee-700 shadow-md transition-colors hover:bg-cream-100 dark:bg-coffee-900 dark:text-cream-300 dark:hover:bg-coffee-800"
          >
            <Menu size={18} />
          </button>
        )}
        {mobileDrawerOpen && (
          <>
            {/* Backdrop — tap to dismiss. */}
            <button
              type="button"
              data-testid="sidebar-mobile-backdrop"
              aria-label={t("nav.closeSidebar")}
              onClick={() => setMobileDrawerOpen(false)}
              className="fixed inset-0 z-40 cursor-default bg-coffee-900/40 dark:bg-black/60"
            />
            <aside
              data-testid="sidebar-mobile-drawer"
              className="fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] shadow-xl"
            >
              {body}
            </aside>
          </>
        )}
      </>
    );
  }

  // --- Desktop / tablet: in-flow sidebar ------------------------------
  return (
    <div
      data-sidebar-expanded={sidebarExpanded}
      style={{ width: sidebarExpanded ? 256 : 52 }}
      className="h-full shrink-0 transition-[width] duration-200 ease-out"
    >
      {body}
    </div>
  );
}

// Back-compat alias — App.tsx still imports `ActivityBar`. After the
// shell rewires to `<Sidebar />` this alias can be removed.
export { Sidebar as ActivityBar };

interface NavRowProps {
  item: NavItem;
  active: boolean;
  expanded: boolean;
  onClick: () => void;
}

// Shared nav-row styling. Mirrored (intentionally duplicated) in
// `SidebarUtility.tsx::utilityRowClass` so the footer rows visually
// match the nav rows above them. If you tweak one, eyeball the other.
function navRowClass(expanded: boolean, active: boolean): string {
  return cn(
    "flex items-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50 dark:focus-visible:ring-offset-coffee-900",
    // Expanded: full row with label; collapsed: 36×36 icon-only.
    expanded ? "h-9 w-full gap-2 px-3 text-body-sm" : "h-9 w-9 justify-center",
    // Claude-style active state: a stronger background wash with the
    // SAME text colour the hover state uses — no accent-colour shift
    // on the glyph. The row reads as "selected" by its fill alone.
    active
      ? "bg-cream-200 text-coffee-700 dark:bg-coffee-800 dark:text-cream-200"
      : "text-coffee-500 hover:bg-cream-100/60 hover:text-coffee-700 dark:text-cream-500 dark:hover:bg-coffee-800/60 dark:hover:text-cream-300",
  );
}

function NavRow({ item, active, expanded, onClick }: NavRowProps) {
  const { t } = useTranslation();
  const Icon = item.icon;
  const label = t(item.labelKey);
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-current={active ? "page" : undefined}
      data-testid={`activity-bar-${item.mode}`}
      className={navRowClass(expanded, active)}
    >
      <Icon size={20} aria-hidden />
      {expanded && <span className="truncate">{label}</span>}
    </button>
  );
}

